import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Menu, X, ChevronDown, ChevronUp, Instagram, Phone } from "lucide-react";
import { topNav, homeSearchAreas, menuLinks, business } from "../../mock";
import SearchOverlay from "./SearchOverlay";

const menuPreviews = [
  "https://images.unsplash.com/photo-1635108201018-94bbbfac4a79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/8143683/pexels-photo-8143683.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.unsplash.com/photo-1505843795480-5cfb3c03f6ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/7045701/pexels-photo-7045701.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.unsplash.com/photo-1638284457192-27d3d0ec51aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];

const Logo = ({ white }) => (
  <Link to="/" onClick={() => window.scrollTo({ top: 0 })} className="flex items-center select-none">
    <img
      src={white ? "/logo-white.png" : "/logo-black.png"}
      alt="Capital Corner"
      className="h-6 md:h-7 w-auto transition-all duration-500"
    />
  </Link>
);

const Header = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuImg, setMenuImg] = useState(0);
  const [searchOverlay, setSearchOverlay] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // all routes have a full-screen hero at top -> white nav until scrolled
  const overHero = !scrolled;
  const white = overHero;

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? "bg-[#f3f1ec] shadow-sm py-4" : "bg-transparent py-6"
        }`}
      >
        {!scrolled && (
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-transparent" />
        )}
        <div className="max-w-[1700px] mx-auto px-6 md:px-10 relative z-10 flex items-center justify-between">
          <nav className="hidden lg:flex items-center gap-7">
            <Link to="/buy" className={`whitespace-nowrap text-[11px] tracking-luxe-sm font-medium transition-colors hover:text-[#b4936a] ${white ? "text-white" : "text-black"}`}>
              BUY WITH CAPITAL CORNER
            </Link>
            <Link to="/sell" className={`whitespace-nowrap text-[11px] tracking-luxe-sm font-medium transition-colors hover:text-[#b4936a] ${white ? "text-white" : "text-black"}`}>
              SELL WITH CAPITAL CORNER
            </Link>
            <div className="relative">
              <button
                onClick={() => setSearchOpen((v) => !v)}
                className={`whitespace-nowrap flex items-center gap-1 text-[11px] tracking-luxe-sm font-medium transition-colors hover:text-[#b4936a] ${white ? "text-white" : "text-black"}`}
              >
                HOME SEARCH {searchOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
              {searchOpen && (
                <div className="mega-in absolute left-0 top-full mt-5 w-[320px] bg-white shadow-2xl border border-neutral-100 z-50">
                  <ul className="py-2 max-h-[70vh] overflow-y-auto">
                    {homeSearchAreas.map((l) => (
                      <li key={l.label}>
                        <Link
                          to={l.to}
                          data-testid={`home-search-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                          onClick={() => setSearchOpen(false)}
                          className="flex items-center justify-between gap-3 px-6 py-2.5 text-[15px] font-serif-display text-neutral-800 hover:text-[#b4936a] hover:bg-neutral-50 transition-colors"
                        >
                          <span>{l.label}</span>
                          <span className="text-[10px] tracking-luxe font-sans-luxe text-neutral-400">{l.count}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </nav>

          <div className="absolute left-1/2 -translate-x-1/2 flex justify-center">
            <Logo white={white} />
          </div>

          <div className="flex items-center justify-end gap-6 ml-auto lg:ml-0">
            <Link to="/team" data-testid="nav-meet-team" className={`hidden lg:inline whitespace-nowrap text-[11px] tracking-luxe-sm font-medium transition-colors hover:text-[#b4936a] ${white ? "text-white" : "text-black"}`}>
              MEET THE TEAM
            </Link>
            <Link to="/team/omer-reshid" data-testid="nav-meet-omer" className={`hidden lg:inline whitespace-nowrap text-[11px] tracking-luxe-sm font-medium transition-colors hover:text-[#b4936a] ${white ? "text-white" : "text-black"}`}>
              MEET OMER
            </Link>
            <button onClick={() => setSearchOverlay(true)} className={`transition-colors hover:text-[#b4936a] ${white ? "text-white" : "text-black"}`} aria-label="Search">
              <Search size={20} strokeWidth={1.4} />
            </button>
            <button onClick={() => setMenuOpen(true)} className={`transition-colors hover:text-[#b4936a] ${white ? "text-white" : "text-black"}`} aria-label="Menu">
              <Menu size={24} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </header>

      {/* Fullscreen Menu */}
      <div className={`fixed inset-0 z-[60] text-white transition-all duration-500 ${menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        {/* warm gradient backdrop */}
        <div className="absolute inset-0 bg-[#0d0d0d]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(80% 90% at 15% 20%, rgba(180,147,106,0.18), rgba(13,13,13,0) 60%)" }} />

        <div className="relative h-full flex flex-col">
          {/* top bar */}
          <div className="max-w-[1700px] w-full mx-auto px-6 md:px-10 py-8 flex items-center justify-between">
            <Link to="/" onClick={() => setMenuOpen(false)} aria-label="Home">
              <img src="/logo-white.png" alt="Capital Corner" className="h-7 w-auto" />
            </Link>
            <button onClick={() => setMenuOpen(false)} className="hover:text-[#b4936a] transition-colors" aria-label="Close">
              <X size={30} strokeWidth={1.2} />
            </button>
          </div>

          {/* split content */}
          <div className="flex-1 min-h-0 max-w-[1700px] w-full mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch pb-8">
            {/* Links */}
            <div className="min-h-0 overflow-y-auto py-8 lg:py-4 flex flex-col justify-start lg:justify-center">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 md:gap-x-12 gap-y-3 sm:gap-y-2 md:gap-y-2.5">
                {menuLinks.map((link, i) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={() => setMenuOpen(false)}
                    onMouseEnter={() => setMenuImg(i % menuPreviews.length)}
                    className="cc-menu-item font-sans-luxe uppercase tracking-luxe-sm text-base md:text-lg font-medium whitespace-nowrap"
                  >
                    <span className="cc-menu-index">{String(i + 1).padStart(2, "0")}</span>
                    <span className="cc-menu-text" data-text={link.label}>{link.label}</span>
                    <span className="cc-menu-arrow">&#8594;</span>
                  </Link>
                ))}
              </div>
              {/* contact row */}
              <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-8 gap-y-3 text-white/60 text-[12px] tracking-luxe-sm">
                <a href={`tel:${business.contactPhone}`} className="flex items-center gap-2 hover:text-[#b4936a] transition-colors"><Phone size={14} /> {business.contactPhone}</a>
                <a href={business.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#b4936a] transition-colors"><Instagram size={14} /> {business.instagramHandle}</a>
              </div>
            </div>

            {/* Preview panel */}
            <div className="hidden lg:block relative h-full min-h-0 overflow-hidden">
              {menuPreviews.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt="Preview"
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${i === menuImg ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20" />
              <div className="absolute bottom-8 left-8 right-8">
                <p className="text-[10px] tracking-luxe uppercase text-[#e7c9a3] mb-2">{business.tagline}</p>
                <p className="font-serif-display text-3xl font-light">Capital Corner Group</p>
                <p className="text-white/55 text-[12px] tracking-luxe-sm uppercase mt-2">{business.area}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SearchOverlay open={searchOverlay} onClose={() => setSearchOverlay(false)} />
    </>
  );
};

export default Header;
